import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, Optional, Self, ViewChild
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';
import { City, County, LocationService } from '@core/api';
import { SharedModule } from '@shared/modules';

@Component({
  selector: 'net-select-county[city]',
  templateUrl: './select-county.component.html',
  styleUrls: ['./select-county.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectCountyComponent
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SharedModule
  ]
})
export class SelectCountyComponent implements OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<County | null> {

  static nextId = 0;

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'select-county';
  describedBy = '';
  stateChanges = new Subject<void>();

  counties: County[] = [];
  inputCtrl = new UntypedFormControl();

  selectedCity: City;

  @ViewChild('auto') auto: MatAutocomplete;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input() tabIndex;

  @Input() set city(city: City) {
    this.selectedCity = null;
    this.reset();

    if (city) {
      this.loading = true;
      this.selectedCity = city;
      this.inputCtrl.setValue('');
      this.inputCtrl.disable();

      // Init with default list
      this.initDefaultOptions().finally(() => {
        this.loading = false;
        if (!this.disabled) {
          this.inputCtrl.enable();
        }
        this.cdr.detectChanges();
      });
    }
  }

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    if (value) {
      this.inputCtrl.disable();
    } else {
      this.inputCtrl.enable();
    }

    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): County | null { return this._value; }
  set value(value: County | null) {
    this._value = value;
    this.onChange(value);
    this.stateChanges.next();
  }
  private _value: County;

  @HostBinding('id') id = `select-county-${SelectCountyComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedByBinding = this.describedBy;

  get shouldLabelFloat() { return !!this.selectedCity && this.focused || !this.empty; }

  get empty() { return !this.value; }

  private initDefaultOptions(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.locationService.counties({
        filter: {
          cityId: this.selectedCity.cityId
        },
        pageSize: 100
      }).toPromise()
        .then((response) => this.counties = response.data.results)
        .finally(() => resolve());
    });
  }

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private locationService: LocationService,
    private cdr: ChangeDetectorRef,
    @Optional() @Self() public ngControl: NgControl
  ) {
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    // Listen input control value changes and filter results
    this.inputCtrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter(value => this.selectedCity && typeof value === 'string'),
      tap(() => {
        this.loading = true;
        // this.counties = [];
        this.value = null;
      }),
      switchMap(value => this.locationService.counties({
        filter: {
          cityId: this.selectedCity.cityId,
          countyName: value
        },
        pageSize: 100
      })),
      tap(() => this.loading = false)
    ).subscribe((response) => {
      this.counties = response.data.results;
      this.cdr.detectChanges();
    });
  }

  displayName = (county: County) => county ? county.name : '';

  reset() {
    if (this.inputCtrl.pristine) {
      return;
    }

    this.value = null;
    this.counties = [];
    this.inputCtrl.setValue(null);
    this.inputCtrl.markAsPristine();

    if (this.selectedCity) {
      this.initDefaultOptions();
    }
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    this.value = event.option.value;
    this.input.nativeElement?.blur();
  }

  onBlur() {
    // On blur with nothing selected reset component
    if (this.ngControl.dirty && !this.auto.isOpen && !this.value) {
      this.reset();
    }
  }

  onClosed() {
    // On panel close with nothing selected reset component
    if (this.ngControl.dirty && !this.value) {
      this.reset();
    }
  }

  onChange = (_: any) => { };
  onTouched = () => { };
  onContainerClick = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');

  writeValue(value: County) {
    this.value = value;
    this.inputCtrl.setValue(value);
  }

  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }
}
